import type { OfficePaymentPart, PaymentPart } from "../api/types";

/**
 * G-141 (OD-215, owner 2026-10-13): how the customer paid, at the kiosk.
 *
 * The florist taps Cash, Card, or both. Both means the customer paid part in
 * cash and the rest by card: she types only the cash part and the card part
 * is the rest of the total (owner: "Type the cash part"). The server checks
 * the same thing (`settlePaymentSplit`); this is the screen's copy, in cents
 * so a remainder is never off by a rounding.
 */
export interface PaymentChoice {
  cash: boolean;
  card: boolean;
  /** What was typed as the cash part; read only when both are picked. */
  cashStr: string;
}

export const NO_PAYMENT_CHOICE: PaymentChoice = { cash: false, card: false, cashStr: "" };

export type PaymentProblem = "pickMethod" | "nothingToSplit" | "typeCash" | "cashNotBelowTotal";

export type PaymentReading =
  | {
      ok: true;
      parts: PaymentPart[];
      /** The card part of a split, for the screen; null with one method. */
      cardCents: number | null;
    }
  | { ok: false; problem: PaymentProblem; cardCents: null };

/**
 * One line's money in cents, rounded the way the server rounds a sale line
 * (quantity × price, half up). Prices carry cents, quantities up to three
 * decimals, so the product is exact in integers.
 */
export function lineCents(price: number, quantity = 1): number {
  if (!Number.isFinite(price) || !Number.isFinite(quantity)) return 0;
  const priceCents = Math.round(price * 100);
  const qtyMilli = Math.round(quantity * 1000);
  return Math.floor((priceCents * qtyMilli + 500) / 1000);
}

/** "20", "20.5", "20,50" → cents; anything else (or more than 2 decimals) → null. */
export function parseCents(raw: string): number | null {
  const s = raw.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
}

/** The choice as the request's payment parts, or what is still missing. */
export function readPaymentChoice(choice: PaymentChoice, totalCents: number): PaymentReading {
  if (!choice.cash && !choice.card) return { ok: false, problem: "pickMethod", cardCents: null };
  if (!(choice.cash && choice.card)) {
    return { ok: true, parts: [{ method: choice.cash ? "CASH" : "CARD" }], cardCents: null };
  }
  if (totalCents <= 0) return { ok: false, problem: "nothingToSplit", cardCents: null };
  const cashCents = parseCents(choice.cashStr);
  if (cashCents === null || cashCents <= 0) {
    return { ok: false, problem: "typeCash", cardCents: null };
  }
  if (cashCents >= totalCents) {
    return { ok: false, problem: "cashNotBelowTotal", cardCents: null };
  }
  const cardCents = totalCents - cashCents;
  return {
    ok: true,
    parts: [
      { method: "CASH", amountEur: cashCents / 100 },
      { method: "CARD", amountEur: cardCents / 100 },
    ],
    cardCents,
  };
}

/**
 * The office "Add a sale" (OD-215): office sales are "usually fully paid
 * online", so Online is on to start and stands alone — paid in full remotely
 * (a card payment link, a bank transfer, Bizum or the Shopify checkout). Cash
 * and Card behave as on the kiosk, alone or together.
 */
export interface OfficePaymentChoice extends PaymentChoice {
  online: boolean;
}

export const OFFICE_PAYMENT_DEFAULT: OfficePaymentChoice = { online: true, cash: false, card: false, cashStr: "" };

/**
 * For a florist the office form starts with nothing on (owner, 2026-10-13):
 * a florist's sale is a counter sale, so they pick cash or card.
 */
export const OFFICE_PAYMENT_NONE: OfficePaymentChoice = { online: false, cash: false, card: false, cashStr: "" };

/** Tapping Online clears Cash and Card; tapping either of those clears Online. */
export function toggleOfficePayment(
  choice: OfficePaymentChoice,
  tapped: "online" | "cash" | "card",
): OfficePaymentChoice {
  if (tapped === "online") return { ...choice, online: !choice.online, cash: false, card: false };
  return { ...choice, online: false, [tapped]: !choice[tapped] };
}

export type OfficePaymentReading =
  | { ok: true; parts: OfficePaymentPart[]; cardCents: number | null }
  | { ok: false; problem: PaymentProblem; cardCents: null };

export function readOfficePaymentChoice(
  choice: OfficePaymentChoice,
  totalCents: number,
): OfficePaymentReading {
  if (choice.online) return { ok: true, parts: [{ method: "ONLINE" }], cardCents: null };
  return readPaymentChoice(choice, totalCents);
}

